/**
 * Utility to crop images from bounding boxes
 */

/**
 * Crop an image using a bounding box
 * 
 * @param {string} imageBase64 - Base64 encoded source image
 * @param {Array} bbox - Bounding box coordinates [x1, y1, x2, y2]
 * @param {string} format - Output format ('png' or 'jpeg')
 * @returns {Promise<string>} Base64 encoded cropped image
 */
export function cropImageFromBbox(imageBase64, bbox, format = 'jpeg') {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const [x1, y1, x2, y2] = bbox;
      
      // Calculate crop dimensions
      const cropWidth = x2 - x1;
      const cropHeight = y2 - y1;
      
      // Create canvas for cropping
      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext('2d');
      
      // Draw the cropped portion
      ctx.drawImage(
        img,
        x1, y1, cropWidth, cropHeight,  // Source rectangle
        0, 0, cropWidth, cropHeight      // Destination rectangle
      );
      
      // Convert to base64
      if (format === 'png') {
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      } else {
        canvas.toBlob(
          (blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.readAsDataURL(blob);
          },
          'image/jpeg',
          0.95
        );
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    
    // Load the image
    if (imageBase64.startsWith('data:')) {
      img.src = imageBase64;
    } else {
      img.src = `data:image/jpeg;base64,${imageBase64}`;
    }
  });
}

/**
 * Generate preview thumbnails from bounding boxes
 * Used for displaying detected objects as mask options
 * 
 * @param {string} imageBase64 - Base64 encoded source image
 * @param {Array} bboxes - Array of bounding boxes
 * @param {number} previewSize - Maximum dimension for preview (default 256)
 * @returns {Promise<Array>} Array of preview objects with base64 images
 */
export async function generatePreviewsFromBboxes(imageBase64, bboxes, previewSize = 256) {
  const previews = [];
  
  for (let i = 0; i < bboxes.length; i++) {
    try {
      const bbox = bboxes[i];
      const [x1, y1, x2, y2] = bbox;
      
      // Crop the image using bbox
      const croppedBase64 = await cropImageFromBbox(imageBase64, bbox, 'png');
      
      // Resize for preview if needed
      const resizedPreview = await resizeImage(croppedBase64, previewSize);
      
      previews.push({
        index: i + 1,
        preview: `data:image/png;base64,${resizedPreview}`,
        bbox: bbox,
        area: (x2 - x1) * (y2 - y1)  // Calculate area for sorting/filtering
      });
    } catch (error) {
      console.error(`Failed to generate preview for bbox ${i}:`, error);
    }
  }
  
  return previews;
}

/**
 * Resize an image to fit within maxSize while maintaining aspect ratio
 * 
 * @param {string} imageBase64 - Base64 encoded image
 * @param {number} maxSize - Maximum dimension
 * @returns {Promise<string>} Resized base64 image
 */
function resizeImage(imageBase64, maxSize) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let width = img.width;
      let height = img.height;
      
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      
      // Create canvas for resizing
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Draw resized image
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      
      // Return as base64
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      resolve(base64);
    };
    
    img.onerror = () => reject(new Error('Failed to load image for resizing'));
    
    // Load the image
    if (imageBase64.startsWith('data:')) {
      img.src = imageBase64;
    } else {
      img.src = `data:image/png;base64,${imageBase64}`;
    }
  });
}